/**
 * SiteFloorPlanSection — orquestador del cuadrante de plano en SiteConfigView.
 *
 * Junta los 4 componentes del sprint planograma:
 *   - FloorSelector vertical (solo si el sitio tiene >1 piso)
 *   - GeocodeSearchBar para encontrar la dirección del sitio
 *   - PlanGeoOverlay (mode='edit' siempre acá)
 *   - Banner de georreferenciación cuando is_georeferenced=false
 *
 * Reemplaza el FloorPlanCanvas (Konva) viejo, que solo soportaba un plano
 * por sitio sin geo.
 *
 * Estado interno:
 *   - floors: lista cargada desde /api/site-floor-plans/{site_id}/floors
 *   - activeFloorId: piso seleccionado en el selector
 *   - activeFloor: objeto del piso activo
 *   - corners pendientes: working copy mientras el operador arrastra handles
 *
 * Callbacks al padre (SiteConfigView):
 *   - onSelectCamera(cameraId)
 *   - onCamerasChanged()         — forzar reload de cameras del piso
 *   - onActiveFloorChange(floor) — para que el padre sepa qué piso filtrar
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import FloorSelector from './FloorSelector'
import GeocodeSearchBar from './GeocodeSearchBar'
import PlanGeoOverlay from './PlanGeoOverlay'


export default function SiteFloorPlanSection({
  siteId,
  token,
  cameras = [],
  selectedCameraId,
  onSelectCamera,
  onActiveFloorChange,
  onCamerasChanged,
  placingCameraId: placingCameraIdProp,
  onPlacingCameraChange,
  height = 560,
}) {
  const [floors, setFloors]               = useState([])
  const [activeFloorId, setActiveFloorId] = useState(null)
  const [imageBlobUrl, setImageBlobUrl]   = useState(null)
  const [loading, setLoading]             = useState(false)
  const [err, setErr]                     = useState(null)
  const [savingGeo, setSavingGeo]         = useState(false)
  const [centerOverride, setCenterOverride] = useState(null)
  // Click-to-place: cuando el operador clickea una cámara sin posición en
  // el strip, "arma" la cámara para colocar. El próximo click sobre el
  // mapa la posiciona ahí. Null = sin cámara armada (click normal).
  const [placingCameraId, setPlacingCameraId] = useState(null)

  // ── Cargar pisos del sitio ─────────────────────────────────────────────
  const reload = useCallback(async () => {
    if (!siteId || !token) return
    setLoading(true); setErr(null)
    try {
      const r = await fetch(`/api/site-floor-plans/${siteId}/floors`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok) {
        if (r.status === 404) { setFloors([]); return }
        throw new Error(`HTTP ${r.status}`)
      }
      const data = await r.json()
      const arr = Array.isArray(data) ? data : []
      setFloors(arr)
      // Si no hay piso activo o el activo desapareció, elegir el menor
      // (típicamente planta baja o único).
      setActiveFloorId(prev => {
        if (prev && arr.find(f => f.id === prev)) return prev
        if (arr.length === 0) return null
        const sorted = [...arr].sort(
          (a, b) => (a.floor_order ?? 0) - (b.floor_order ?? 0),
        )
        return sorted[0].id
      })
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [siteId, token])

  useEffect(() => { reload() }, [reload])

  const activeFloor = useMemo(
    () => floors.find(f => f.id === activeFloorId) || null,
    [floors, activeFloorId],
  )

  // Notificar al padre cuando cambia el piso activo
  useEffect(() => {
    if (onActiveFloorChange) onActiveFloorChange(activeFloor)
  }, [activeFloor, onActiveFloorChange])

  // ── Cargar imagen del piso activo ──────────────────────────────────────
  useEffect(() => {
    if (!activeFloor || !token) {
      setImageBlobUrl(null)
      return
    }
    let cancelled = false
    let createdUrl = null
    ;(async () => {
      try {
        const r = await fetch(`/api/site-floor-plans/${activeFloor.id}/image`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const blob = await r.blob()
        if (cancelled) return
        createdUrl = URL.createObjectURL(blob)
        setImageBlobUrl(createdUrl)
      } catch (e) {
        if (!cancelled) setErr(`Cargando imagen del plano: ${e.message}`)
      }
    })()
    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [activeFloor?.id, token])

  // Cámaras del piso activo
  const floorCameras = useMemo(() => {
    if (!activeFloor) return []
    return cameras.filter(c => c.floor_plan?.id === activeFloor.id)
  }, [cameras, activeFloor])

  // ── Guardar georreferenciación ─────────────────────────────────────────
  const handleGeoreferenceChange = useCallback(async (newCorners) => {
    if (!activeFloor) return
    // Si las 4 esquinas todavía no están definidas (plano nunca calzado),
    // el operador apenas está empezando a arrastrar — guardar localmente
    // sin pegarle al backend en cada drag. Acá ya recibimos las 4 finales,
    // entonces sí pegamos.
    setSavingGeo(true)
    try {
      const body = JSON.stringify({
        nw: newCorners.nw,
        ne: newCorners.ne,
        sw: newCorners.sw,
        se: newCorners.se,
        rotation_deg: newCorners.rotation_deg ?? null,
      })
      const r = await fetch(`/api/site-floor-plans/${activeFloor.id}/georeference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      })
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}))
        throw new Error(detail.detail || `HTTP ${r.status}`)
      }
      await reload()
    } catch (e) {
      setErr(`Guardando georreferencia: ${e.message}`)
    } finally {
      setSavingGeo(false)
    }
  }, [activeFloor, token, reload])

  // ── Inicializar esquinas por default cuando el plano no tiene aún ──────
  // Si el plano nunca fue georreferenciado, le damos un footprint inicial
  // de ~80×60m alrededor del centro del mapa actual (o el centroOverride
  // si el operador buscó una dirección). El operador después ajusta.
  const ensureInitialCorners = useCallback(() => {
    if (!activeFloor || activeFloor.is_georeferenced) return
    const center = centerOverride || { lat: -33.4569, lon: -70.6483 }
    // ~80m W-E (depende de la latitud, pero acá aproximamos) y 60m N-S
    // 1° lat ≈ 111km. 80m = 0.00072°. 60m = 0.00054°.
    const halfLat = 0.00027   // ~30m mitad
    const halfLon = 0.00036   // ~40m mitad
    const guess = {
      nw: [center.lat + halfLat, center.lon - halfLon],
      ne: [center.lat + halfLat, center.lon + halfLon],
      sw: [center.lat - halfLat, center.lon - halfLon],
      se: [center.lat - halfLat, center.lon + halfLon],
    }
    // Inyectamos en floors local para que PlanGeoOverlay lo muestre.
    // El backend NO se actualiza hasta que el operador arrastre y suelte.
    setFloors(prev => prev.map(f => f.id === activeFloor.id
      ? { ...f, georeference: { ...guess, meters_per_pixel: null, rotation_deg: null } }
      : f,
    ))
  }, [activeFloor, centerOverride])

  useEffect(() => {
    // Cuando cambia el piso activo y no está georreferenciado, semilla
    // inicial para que el overlay tenga algo que mostrar.
    if (activeFloor && !activeFloor.is_georeferenced && !activeFloor.georeference) {
      ensureInitialCorners()
    }
  }, [activeFloor, ensureInitialCorners])

  // Persistir posición de cámara en backend (drop desde sidebar o re-drag
  // dentro del plano). Incluye floor_plan_id para que el backend asigne
  // la cámara al piso y calcule lat/lon proyectando sobre las 4 esquinas
  // georeferenciadas.
  const persistCameraPosition = useCallback(async (cameraId, xPct, yPct) => {
    if (!activeFloor) return
    try {
      const r = await fetch(`/api/cameras/${cameraId}/position`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          x_pct: xPct,
          y_pct: yPct,
          rotation_deg: 0,
          floor_plan_id: activeFloor.id,
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      // El padre debería recargar cameras para que aparezca en el mapa.
      if (onCamerasChanged) onCamerasChanged()
    } catch (e) {
      setErr(`Posicionando cámara: ${e.message}`)
    }
  }, [activeFloor, token, onCamerasChanged])

  // ── Handler buscador ───────────────────────────────────────────────────
  const handleSearchSelect = useCallback(async (result) => {
    // Centramos el mapa en la dirección encontrada
    setCenterOverride({ lat: result.lat, lon: result.lon })
    if (!activeFloor) return

    // Re-semilla las 4 esquinas en la nueva ubicación.
    const halfLat = 0.00027
    const halfLon = 0.00036
    const guess = {
      nw: [result.lat + halfLat, result.lon - halfLon],
      ne: [result.lat + halfLat, result.lon + halfLon],
      sw: [result.lat - halfLat, result.lon - halfLon],
      se: [result.lat - halfLat, result.lon + halfLon],
    }
    // Update local primero (UI reactiva al instante).
    setFloors(prev => prev.map(f => f.id === activeFloor.id
      ? { ...f, georeference: { ...guess, meters_per_pixel: null, rotation_deg: null } }
      : f,
    ))
    // Persistir al backend INMEDIATAMENTE — sin esto, drops posteriores
    // proyectarían contra esquinas viejas y las cámaras quedarían fuera de
    // vista. El operador después puede ajustar las 4 esquinas manualmente.
    try {
      const r = await fetch(`/api/site-floor-plans/${activeFloor.id}/georeference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(guess),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      // Recargar pisos + cámaras (las cámaras re-calculan lat/lon)
      await reload()
      if (onCamerasChanged) onCamerasChanged()
    } catch (e) {
      setErr(`Guardando ubicación: ${e.message}`)
    }
  }, [activeFloor, token, reload, onCamerasChanged])

  // ── Render ─────────────────────────────────────────────────────────────

  if (loading && floors.length === 0) {
    return <div style={{ padding: 20, color: 'var(--text-muted)' }}>Cargando plano…</div>
  }

  if (floors.length === 0) {
    return (
      <div style={{
        padding: 30, textAlign: 'center',
        background: 'var(--bg-elev-1)', border: '1px dashed var(--border)',
        borderRadius: 8, color: 'var(--text-muted)',
      }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>📐</div>
        <div style={{ fontSize: 14, marginBottom: 4 }}>Este sitio aún no tiene plano cargado</div>
        <div style={{ fontSize: 12 }}>
          Subí el plano de cada piso en la sección de carga (PDF, PNG o JPG).
        </div>
      </div>
    )
  }

  // Plano para PlanGeoOverlay — incluye la georeference (real o semilla)
  const planForOverlay = activeFloor
    ? { id: activeFloor.id, georeference: activeFloor.georeference }
    : null

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height,
      background: '#1f2937',
      borderRadius: 8,
      overflow: 'hidden',
      border: '1px solid var(--border)',
    }}>
      {/* Buscador encima del mapa */}
      <div style={{
        position: 'absolute',
        top: 12, left: 12,
        width: '60%', maxWidth: 480,
        zIndex: 11,
      }}>
        <GeocodeSearchBar token={token} onSelect={handleSearchSelect} />
      </div>

      {/* Selector vertical de pisos a la derecha */}
      <FloorSelector
        floors={floors}
        activeFloorId={activeFloorId}
        onSelect={setActiveFloorId}
      />

      {/* Banner amarillo si el piso no está georreferenciado */}
      {activeFloor && !activeFloor.is_georeferenced && (
        <div style={{
          position: 'absolute',
          bottom: 12, left: 12, right: activeFloor && floors.length > 1 ? 96 : 12,
          padding: '10px 14px',
          background: 'rgba(217, 119, 6, 0.95)',
          color: 'white',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          zIndex: 11,
        }}>
          Este piso aún no está georreferenciado. Arrastrá las 4 esquinas azules
          (NW, NE, SW, SE) hasta calzar el plano sobre el edificio en el mapa.
          {savingGeo && <span style={{ marginLeft: 12, opacity: 0.8 }}>(guardando…)</span>}
        </div>
      )}

      {/* Banner verde si está georreferenciado */}
      {activeFloor && activeFloor.is_georeferenced && (
        <div style={{
          position: 'absolute',
          bottom: 12, left: 12,
          padding: '6px 12px',
          background: 'rgba(34, 197, 94, 0.95)',
          color: 'white',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 500,
          zIndex: 11,
        }}>
          Georreferenciado · {activeFloor.georeference?.meters_per_pixel?.toFixed(3)} m/px
        </div>
      )}

      {err && (
        <div style={{
          position: 'absolute',
          top: 60, left: 12, right: 12,
          padding: '8px 14px',
          background: 'rgba(217, 48, 37, 0.95)',
          color: 'white',
          borderRadius: 6,
          fontSize: 12,
          zIndex: 11,
        }}>
          {err}
        </div>
      )}

      {/* El mapa MapLibre + plano + handles */}
      {planForOverlay && imageBlobUrl && (
        <PlanGeoOverlay
          mode="edit"
          floorPlan={planForOverlay}
          imageUrl={imageBlobUrl}
          cameras={floorCameras}
          selectedCameraId={selectedCameraId}
          initialCenter={centerOverride}
          height="100%"
          onGeoreferenceChange={handleGeoreferenceChange}
          onCameraSelect={onSelectCamera ? (cam) => onSelectCamera(cam.id) : undefined}
          onCameraMove={async (cameraId, xPct, yPct) => {
            // Re-posicionar cámara que ya estaba sobre el plano (drag final).
            await persistCameraPosition(cameraId, xPct, yPct)
          }}
          onCameraDrop={async (cameraId, xPct, yPct) => {
            // Drop desde el sidebar — primera vez que se posiciona en este piso.
            await persistCameraPosition(cameraId, xPct, yPct)
          }}
          placingCameraId={placingCameraIdProp}
          onCameraPlaced={async (cameraId, xPct, yPct) => {
            // Click-to-place: el operador "armó" una cámara y clickeó sobre el mapa.
            await persistCameraPosition(cameraId, xPct, yPct)
            if (onPlacingCameraChange) onPlacingCameraChange(null)
          }}
        />
      )}
    </div>
  )
}
